// Formatter to map backend API models to what the frontend UI components expect.

/**
 * Normalizes the backend product response into the structure expected by the frontend.
 * 
 * Backend `GET /api/products` response structure per item (example):
 * {
 *   id: 1,
 *   name: 'Healthy Home Cold Pressed Sesame Oil 1LTR',
 *   price: 499,
 *   stock: 50,
 *   Variants: [
 *      { variantName: '1LTR', mrp: 599, salesPrice: 499, stock: 50, unit: '1LTR' } // etc
 *   ],
 *   category: ["Cold Pressed Oils"],
 *   tag: ["Sesame Oil 1LTR"],
 *   image: ["uploads/products/..."],
 *   discount: 10,
 *   rating: 4.8,
 *   reviewCount: 127
 * }
 * 
 * Frontend expects (example):
 * {
 *   id: 1,
 *   image: "assets/images/thumbs/product-img1.png",
 *   originalPrice: "₹599",
 *   currentPrice: "₹499",
 *   quantityText: "/ 1LTR",
 *   rating: "4.8",
 *   reviewCount: "127",
 *   name: "Healthy Home Cold Pressed Sesame Oil 1LTR",
 *   seller: "Healthy Home Foods",
 *   sold: 28,
 *   totalSold: 35,
 *   progress: 80,
 *   category: "Cold Pressed Oils",
 *   tags: ["Sesame Oil 1LTR"]
 * }
 */

import { formatImageUrl } from '../utils/formatImageUrl.js';

const getInitialImage = (backendImageArray) => {
    if (backendImageArray && backendImageArray.length > 0) {
        return backendImageArray[0];
    }
    return '';
};

export const generateSlug = (name) => {
    if (!name) return "";
    return name.toString().toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w\-]+/g, '')
        .replace(/\-\-+/g, '-')
        .replace(/^-+/, '')
        .replace(/-+$/, '');
};

export const formatProductData = (backendProduct) => {
    // If there's a variant, we can pull mrp and salesPrice from the first variant
    // If not, we fall back to top-level price / discount
    let originalPrice = backendProduct.price || 0;
    let currentPrice = backendProduct.price || 0;
    let quantityText = "";
    let variantDisplay = "";

    const variants = backendProduct.Variants || [];
    let defaultVariantIndex = 0;
    if (variants.length > 0) {
        const foundIdx = variants.findIndex(v => v.isDefault === true || v.is_default === true || String(v.isDefault) === 'true' || String(v.is_default) === 'true');
        if (foundIdx !== -1) {
            defaultVariantIndex = foundIdx;
        }
        const defaultVar = variants[defaultVariantIndex];
        originalPrice = defaultVar.mrp || defaultVar.salesPrice || currentPrice;
        currentPrice = defaultVar.salesPrice || currentPrice;

        let unit = "";

        let attrs = [];
        if (defaultVar.attributes) {
            try {
                attrs = typeof defaultVar.attributes === 'string' ? JSON.parse(defaultVar.attributes) : defaultVar.attributes;
            } catch (e) { }
        }

        if (Array.isArray(attrs) && attrs.length > 0) {
            // Extract only the values (e.g., '500 ML' instead of 'Custom Note: 500 ML')
            unit = attrs.map(a => a.value).filter(Boolean).join(' | ');
        } else {
            // Fallback for older products
            unit = defaultVar.variantName || defaultVar.unit || "";
            unit = unit.replace(/(?:Custom Note|Volume|Weight|Size|Colour|Color|Variant)\s*:\s*/gi, '').trim();
            if (unit.toLowerCase() === 'default' || unit.toLowerCase() === 'free size') {
                unit = "";
            }
        }

        // AUTO-CORRECT: If this is a liquid/oil product but the admin accidentally set unit to G/KG
        const nameLower = (backendProduct.name || "").toLowerCase();
        const catLower = (backendProduct.Category?.name || backendProduct.Category?.label || "").toLowerCase();
        const isLiquid = nameLower.includes("oil") || nameLower.includes("milk") || nameLower.includes("ghee") || catLower.includes("oil") || catLower.includes("dairy");

        if (isLiquid && /G|KG/i.test(unit)) {
            // Attempt to extract the correct volume from the product name (e.g. "500ML")
            const match = nameLower.match(/(\d+\.?\d*)\s*(ml|ltr|l)\b/i);
            if (match) {
                unit = (match[1] + match[2]).toUpperCase();
            } else {
                // If it can't be found in the name, fallback replace G -> ML and KG -> LTR
                unit = unit.toUpperCase().replace("KG", "LTR").replace("G", "ML");
            }
        }

        variantDisplay = "";
        if (unit) {
            const formattedUnit = unit.replace(/(\d+)\s*([a-zA-Z]+)/g, '$1 $2').replace(/\s+/g, ' ').trim().toUpperCase();
            quantityText = `/ ${formattedUnit}`;

            if (formattedUnit.includes('LTR') || formattedUnit.includes('ML') || formattedUnit.match(/\bL\b/)) {
                variantDisplay = `Volume: ${formattedUnit}`;
            } else if (formattedUnit.includes('KG') || formattedUnit.includes('G')) {
                variantDisplay = `Weight: ${formattedUnit}`;
            } else {
                variantDisplay = `Variant: ${formattedUnit}`;
            }
        }
    } else {
        // Guess original price via discount if no variants
        if (backendProduct.discount) {
            originalPrice = currentPrice / (1 - (backendProduct.discount / 100));
        }
    }

    let rawImage = getInitialImage(backendProduct.image);
    let imageStr = formatImageUrl(rawImage, '/assets/images/thumbs/product-img1.png');

    // Handle category / tag format correctly (Backend arrays -> Frontend string/array)
    let singleCategory = "All";
    let categoryId = null;

    if (backendProduct.Category) {
        singleCategory = backendProduct.Category.name || backendProduct.Category.label || "All";
        categoryId = backendProduct.Category.id;
    } else if (Array.isArray(backendProduct.category) && backendProduct.category.length > 0) {
        // Fallback for older products/data structures
        categoryId = backendProduct.category[0];
        singleCategory = categoryId; // Will be mapped by context later if needed
    }

    let tags = [];
    if (Array.isArray(backendProduct.tag)) {
        tags = backendProduct.tag;
    }

    let productGallery = [];
    if (Array.isArray(backendProduct.image)) {
        productGallery = backendProduct.image.map(img => formatImageUrl(img, '/assets/images/thumbs/product-img1.png')).filter(Boolean);
    } else if (imageStr) {
        productGallery = [imageStr];
    }

    // Normalize variant images to be absolute URLs
    const normalizedVariants = variants.map(v => {
        const nv = { ...v };
        if (nv.image) {
            nv.image = formatImageUrl(nv.image, '/assets/images/thumbs/product-img1.png');
        }

        let rawImgs = nv.images;
        if (typeof rawImgs === 'string') {
            try { rawImgs = JSON.parse(rawImgs); } catch { rawImgs = []; }
        }
        if (Array.isArray(rawImgs)) {
            nv.images = rawImgs.map(img => formatImageUrl(img, '/assets/images/thumbs/product-img1.png')).filter(Boolean);
        } else {
            nv.images = [];
        }
        return nv;
    });

    return {
        ...backendProduct,
        id: backendProduct.id,
        image: imageStr || "assets/images/thumbs/product-img1.png", // fallback
        gallery: productGallery.length > 0 ? productGallery : ["assets/images/thumbs/product-img1.png"],
        originalPrice: `₹${Math.round(originalPrice)}`,
        currentPrice: `₹${Math.round(currentPrice)}`,
        price: typeof currentPrice === 'number' ? currentPrice : parseFloat(String(currentPrice).replace(/[^0-9.]/g, '')) || 0,
        shippingWeight: backendProduct.shippingWeight !== null && backendProduct.shippingWeight !== undefined ? backendProduct.shippingWeight : null,
        shippingDimensions: backendProduct.shippingDimensions !== null && backendProduct.shippingDimensions !== undefined ? backendProduct.shippingDimensions : null,
        quantityText: quantityText,
        variantDisplay: variantDisplay,
        rating: backendProduct.rating || "4.5",
        reviewCount: backendProduct.reviewCount || "0",
        name: backendProduct.name,
        seller: backendProduct.Brand?.name || "Healthy Home Foods",
        sold: backendProduct.saleCount || 0,
        totalSold: 35,
        progress: backendProduct.saleCount ? Math.min(100, (backendProduct.saleCount / 35) * 100) : 50,
        category: singleCategory,
        categoryId: categoryId,
        subCategoryId: backendProduct.subCategoryId || backendProduct.sub_category_id || null,
        tags: tags,
        slug: backendProduct.slug || generateSlug(backendProduct.name),
        isRecommended: backendProduct.isRecommended || false,
        isNewArrival: backendProduct.isNew || false,
        isHotDeal: backendProduct.isHotDeal || false,
        isFamilyPack: backendProduct.isFamilyPack || false,
        defaultVariantIndex: defaultVariantIndex,
        defaultVariant: normalizedVariants[defaultVariantIndex] || null,
        Variants: normalizedVariants,
        rawVariants: normalizedVariants,
        rawModel: backendProduct
    };
};

export const formatVariant = (variant) => {
    return {
        ...variant
    }
}
